/**
 * AskArgoDrawer — the agent as a presence, not a place (UX_REVIEW move #2).
 *
 * A right-docked panel that hosts a live Argo conversation on ANY view, so
 * asking the agent about what you're looking at no longer means navigating
 * away to the Chat view. It runs the exact same machinery as ChatView —
 * useAgentConversation (interpret → confirm-each-write → undo → memory
 * signals) + the shared MessageBubble/ActionCard — so every governance
 * guarantee holds identically here.
 *
 * Design decisions:
 * - NO backdrop: the whole point is answering *beside* the work, so the
 *   page behind stays visible and scrollable. Escape closes only while
 *   focus is inside the drawer (container onKeyDown, not a window handler),
 *   so it can't fight other Escape-closable surfaces.
 * - Its own transcript, separate from the Chat view's: a docked aside
 *   shouldn't rewrite the main thread's history. "Open full chat" is an
 *   honest navigation — the side conversation stays here.
 * - View-aware starter prompts: each view seeds prompts that map onto
 *   intents the backend already understands, so the drawer is context-aware
 *   from the first click even before entity-level context wiring exists.
 * - z-index 70: below the Tickets detail drawer (80) and the ⌘K palette
 *   (90), so those still win when opened on top.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Bot,
  CircleDashed,
  CornerDownLeft,
  Expand,
  Loader2,
  Lock,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import { isConfigured as workerConfigured } from '../lib/agent/argoWorker';
import {
  fmtTime,
  useAgentConversation,
  type ChatMessage,
} from '../lib/agent/useAgentConversation';
import { MessageBubble } from './AgentMessages';
import { ArgoSuggestsStrip } from './ArgoSuggestsStrip';
import { ArgoOffersRow } from './ArgoOffersRow';
import { useProactiveNudges } from '../lib/agent/useProactiveNudges';
import { queueApproval } from '../lib/agent/approvals';
import type { Standup } from '../lib/agent/standup';
import type { GreetingChip, SessionGreeting } from '../lib/agent/sessionGreeting';
import type { ViewKey } from './TopMenu';

/** The read-only sweep the "Sweep for risks" chip sends — the existing
 *  interpret path runs the system scanner and reports ranked findings. */
export const SWEEP_PROMPT = 'What needs my attention today?';

/** What the "Research a prospect" chip prefills (real Claude worker path). */
export const RESEARCH_PREFILL = 'Research prospect ';

const GREETING: ChatMessage = {
  id: 'greet',
  who: 'argo',
  body:
    'I’m right here — ask about what you’re looking at, or tell me what to do. Writes always come back as cards for you to confirm.',
  when: fmtTime(),
};

/**
 * Starter prompts per view. Every prompt maps onto an intent the backend
 * already understands (write parsers or the read-only data answerer), so
 * the drawer never suggests something the simulation can't handle.
 */
const VIEW_PROMPTS: Partial<Record<ViewKey, string[]>> = {
  dashboard: [
    'What needs my attention today?',
    'What is our pipeline and ARR?',
  ],
  contacts: [
    'Who is our biggest deal?',
    'Add prospect Jane Doe at Acme, deal worth $48k',
    'Follow up with Jane Doe next week',
  ],
  'customer-success': [
    'List our customers',
    'What needs my attention today?',
  ],
  tickets: [
    'How many open tickets are there?',
    'Open a ticket to fix the billing export (p1)',
  ],
  projects: [
    'What needs my attention today?',
    'Open a ticket to fix the billing export (p1)',
  ],
  contracts: [
    'Create a proposal for Argo IQ',
    'What needs my attention today?',
  ],
  files: [
    'Create a proposal for Argo IQ',
  ],
  'content-engine': [
    'Who is our biggest deal?',
  ],
};

const DEFAULT_PROMPTS: string[] = [
  'What needs my attention today?',
  'Add prospect Jane Doe at Acme, deal worth $48k',
];

export interface AskArgoDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The view behind the drawer — seeds the context-aware starter prompts. */
  view: ViewKey;
  /** A query handed in (e.g. a Dashboard "Ask Argo" button or the ⌘K
   *  palette). When the nonce changes, the drawer auto-sends the text. */
  query?: { text: string; nonce: number } | null;
  /** Navigate to the full Chat view (the drawer's transcript stays here). */
  onOpenFullChat: () => void;
  /**
   * The morning standup (composeStandup) — when present, it renders as the
   * FIRST agent message with [Review one-by-one] [Approve all reversible]
   * [Not now] chips. Every count in it is real (the host gathered them via
   * gatherStandupInputs); the host stamps lastStandupShownAt on any chip
   * AND on manual close, so it can never recur the same day.
   */
  standup?: Standup | null;
  /** Chip: walk focus to the first item's surface (Home do-now). */
  onStandupReview?: () => void;
  /** Chip: approve every reversible pending proposal (existing batch lane). */
  onStandupApproveAll?: () => void;
  /** Chip: close + stamp — "Not now" is always one click away. */
  onStandupDismiss?: () => void;
  /** Cold start: open guided setup (Admin → Setup) to fill the Brain + book. */
  onStandupSetup?: () => void;
  /**
   * The session greeting for the lighter variants — 'while-away' /
   * 'nudges' / 'all-clear' (composeSessionGreeting). Variants (a)/(d)
   * carry a Standup and ride in via `standup` instead, so the tested
   * standup card renders unchanged. Never both at once.
   */
  greeting?: SessionGreeting | null;
  /** Greeting chip: navigate to the Content Engine to draft this week's posts. */
  onGreetingDraftPosts?: () => void;
  /** Any greeting chip acted on → the host stamps lastSeenFeedAt + clears. */
  onGreetingSettle?: () => void;
}

export function AskArgoDrawer({
  open,
  onClose,
  view,
  query = null,
  onOpenFullChat,
  standup = null,
  onStandupReview,
  onStandupApproveAll,
  onStandupDismiss,
  onStandupSetup,
  greeting = null,
  onGreetingDraftPosts,
  onGreetingSettle,
}: AskArgoDrawerProps) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const conv = useAgentConversation(GREETING);
  const [composer, setComposer] = useState('');
  // Proactive nudges — the same tested engine behind every "Argo suggests"
  // strip, so the dock leads with the agent's top suggestions on ANY view.
  // Accepting one queues the governed action into the approvals inbox.
  const nudges = useProactiveNudges();
  // Greeting nudge chips → the SAME governed lane as ArgoSuggestsStrip
  // (queueApproval into the Dashboard inbox); a queued chip flips its label
  // instead of pretending the work is done.
  const [queuedChips, setQueuedChips] = useState<Record<string, boolean>>({});

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [conv.messages, open]);

  // Auto-send an incoming query (nonce-keyed, same pattern as ChatView's
  // initialQuery) — but only while the drawer is open, so a stale query
  // can't fire into a closed panel.
  const lastNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!open || !query || query.nonce === lastNonce.current) return;
    lastNonce.current = query.nonce;
    void conv.send(query.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  if (!open) return null;

  const chipStyle = {
    cursor: 'pointer',
    border: '1px solid rgba(255,0,255,0.22)',
  } as const;

  // One greeting chip → one REAL action. Nudge chips queue the nudge's
  // governed action (same lane as ArgoSuggestsStrip); the offer chips wire
  // to the Content Engine, the existing scanner-backed "needs attention"
  // answer, and the composer's research ask.
  const greetingChip = (chip: GreetingChip) => {
    if (chip.kind === 'nudge') {
      const live = nudges.find((n) => n.id === chip.nudgeId);
      const queued = !!queuedChips[chip.nudgeId];
      return (
        <button
          key={`nudge:${chip.nudgeId}`}
          type="button"
          className={queued ? 'glass-pill glass-pill--green' : 'glass-pill'}
          disabled={!live || queued}
          title={
            live
              ? `${live.headline} — queues this governed action for your approval`
              : 'Already resolved since this greeting was composed'
          }
          onClick={() => {
            if (!live || queued) return;
            void queueApproval(live.action, 'autopilot', live.reason)
              .then(() => setQueuedChips((s) => ({ ...s, [chip.nudgeId]: true })))
              .catch(() => undefined);
          }}
          style={queued || !live ? { cursor: 'default' } : chipStyle}
        >
          {queued ? 'Queued — approve in the Dashboard inbox' : chip.label}
        </button>
      );
    }
    if (chip.kind === 'draft-posts') {
      return (
        <button
          key="draft-posts"
          type="button"
          className="glass-pill"
          title="Open the Content Engine and draft this week's posts"
          onClick={onGreetingDraftPosts}
          style={chipStyle}
        >
          {chip.label}
        </button>
      );
    }
    if (chip.kind === 'sweep-risks') {
      return (
        <button
          key="sweep-risks"
          type="button"
          className="glass-pill"
          title="Runs the system scanner over your live book and reports ranked findings"
          onClick={() => {
            onGreetingSettle?.();
            void conv.send(SWEEP_PROMPT);
          }}
          style={chipStyle}
        >
          {chip.label}
        </button>
      );
    }
    // research-prospect — the research path runs on the real Claude worker;
    // simulation mode can't browse, and the chip says so instead of faking.
    const researchReady = workerConfigured();
    return (
      <button
        key="research-prospect"
        type="button"
        className="glass-pill"
        disabled={!researchReady}
        title={
          researchReady
            ? 'Prefills a research ask — name the prospect and send'
            : 'Research needs the real Claude worker (set VITE_AGENT_API_URL) — simulation mode can\u2019t browse the web'
        }
        onClick={() => {
          if (!researchReady) return;
          onGreetingSettle?.();
          setComposer(RESEARCH_PREFILL);
        }}
        style={researchReady ? chipStyle : { cursor: 'default', opacity: 0.7 }}
      >
        {researchReady ? chip.label : `${chip.label} (needs Claude worker)`}
      </button>
    );
  };

  const submit = () => {
    const text = composer.trim();
    if (!text || conv.busy || !canMutate) return;
    setComposer('');
    void conv.send(text);
  };

  const prompts = VIEW_PROMPTS[view] ?? DEFAULT_PROMPTS;
  const fresh = conv.messages.length <= 1;

  return (
    <div
      role="complementary"
      aria-label="Ask Argo"
      onKeyDown={(e) => {
        // Scoped Escape: closes only while focus is inside the drawer, so it
        // never races other Escape-closable overlays (ticket drawer, modals).
        if (e.key === 'Escape') onClose();
      }}
      style={{
        position: 'fixed',
        top: 'calc(var(--topmenu-h) + 16px)',
        right: 16,
        bottom: 16,
        width: 'min(430px, calc(100vw - 32px))',
        zIndex: 70,
        display: 'flex',
      }}
    >
      <GlassPanel
        variant="sharp"
        animate={false}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '14px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'inline-grid',
              placeItems: 'center',
              flex: '0 0 auto',
              background: 'linear-gradient(135deg, #FF00FF, #BB4198)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#fff',
            }}
          >
            <Bot size={15} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="t-card-title" style={{ display: 'block' }}>
              Ask Argo
            </span>
            <span
              className={workerConfigured() ? 'glass-pill glass-pill--green' : 'glass-pill'}
              title={
                workerConfigured()
                  ? 'Chat is interpreted by the real Claude worker (VITE_AGENT_API_URL).'
                  : 'Simulation mode — set VITE_AGENT_API_URL to connect the real Claude worker.'
              }
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 2 }}
            >
              {workerConfigured() ? <Zap size={10} /> : <CircleDashed size={10} />}
              {workerConfigured() ? 'Connected to Claude worker' : 'Simulation mode'}
            </span>
          </div>
          <button
            type="button"
            className="glass-btn"
            onClick={onOpenFullChat}
            title="Open the full Chat view — this side conversation stays here"
            style={{ padding: '6px 8px' }}
          >
            <Expand size={13} />
          </button>
          <button
            type="button"
            className="glass-btn"
            onClick={onClose}
            aria-label="Close Ask Argo"
            style={{ padding: '6px 8px' }}
          >
            <X size={13} />
          </button>
        </div>

        {/* Argo can help — grounded research + reminder offers beneath the
            greeting. Accepting fires the REAL mechanism (research run /
            governed follow-up proposal) and drops a confirmation line into
            the transcript; dismissing hides the offer for 7 days. */}
        <div style={{ padding: '10px 12px 0' }}>
          <ArgoOffersRow canMutate={canMutate} onConfirm={conv.note} />
        </div>

        {/* Proactive nudges — the agent leads with its top suggestions.
            Renders nothing when the book is clean; accepting one routes the
            governed action through the existing approvals inbox. */}
        {nudges.length > 0 && (
          <div style={{ padding: '10px 12px 0' }}>
            <ArgoSuggestsStrip nudges={nudges} limit={2} canMutate={canMutate} compact />
          </div>
        )}

        {/* Transcript */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
            display: 'grid',
            gap: 14,
            alignContent: 'start',
          }}
        >
          {/* Morning standup — the system speaks first, once per day, with
              work that actually exists. Renders BEFORE the transcript so it
              reads as the first agent message; every chip is one click and
              the host stamps the day on any of them (never a trap). */}
          {standup && (
            <div
              role="status"
              aria-label="Morning standup"
              style={{
                borderRadius: 14,
                border: '1px solid rgba(255,0,255,0.22)',
                background: 'rgba(255,0,255,0.06)',
                padding: '12px 14px',
                display: 'grid',
                gap: 8,
              }}
            >
              <span className="t-card-title">{standup.greeting}</span>
              <span className="t-body" style={{ color: C.textMuted }}>
                {standup.coldStart
                  ? "I don't have anything to work on yet — your workspace is empty. " +
                    'Give me your business (voice, customers, invoices, prospects) and ' +
                    "I'll start drafting, chasing, and briefing you every day."
                  : `Ready for you: ${standup.lines.join(', ')} — approve all,
                review one-by-one, or dismiss.`}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {standup.coldStart ? (
                  <button
                    type="button"
                    className="glass-pill"
                    onClick={onStandupSetup}
                    title="Guided setup: your story, voice, customers and goals — the fuel for everything"
                    style={{ cursor: 'pointer', border: '1px solid rgba(255,0,255,0.22)' }}
                  >
                    Set up my business
                  </button>
                ) : (
                <button
                  type="button"
                  className="glass-pill"
                  onClick={onStandupReview}
                  title="Walk the queue on Home — first item first"
                  style={{ cursor: 'pointer', border: '1px solid rgba(255,0,255,0.22)' }}
                >
                  Review one-by-one
                </button>
                )}
                {canMutate && standup.counts.reversible > 0 && (
                  <button
                    type="button"
                    className="glass-pill"
                    onClick={onStandupApproveAll}
                    title="Approve every reversible (undoable) proposal — gated ones stay for manual review"
                    style={{ cursor: 'pointer', border: '1px solid rgba(255,0,255,0.22)' }}
                  >
                    Approve all reversible ({standup.counts.reversible})
                  </button>
                )}
                <button
                  type="button"
                  className="glass-pill"
                  onClick={onStandupDismiss}
                  title="Close — the standup won't come back today"
                  style={{ cursor: 'pointer' }}
                >
                  Not now
                </button>
              </div>
            </div>
          )}
          {/* Session greeting — the lighter variants (while-away / nudges /
              all-clear). EVERY session opens with one of these or the
              standup above; silence is a bug. Chips are real actions:
              nudges queue governed proposals, the offers wire to the
              Content Engine / scanner sweep / research composer. */}
          {greeting && (
            <div
              role="status"
              aria-label="Session greeting"
              style={{
                borderRadius: 14,
                border: '1px solid rgba(255,0,255,0.22)',
                background: 'rgba(255,0,255,0.06)',
                padding: '12px 14px',
                display: 'grid',
                gap: 8,
              }}
            >
              <span className="t-card-title">{greeting.greeting}</span>
              <span className="t-body" style={{ color: C.textMuted }}>
                {greeting.body}
              </span>
              {canMutate && greeting.chips.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {greeting.chips.map(greetingChip)}
                </div>
              )}
            </div>
          )}
          {conv.messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              people={conv.context.people}
              brainFacts={conv.context.brainFacts}
              canMutate={canMutate}
              onConfirm={conv.confirm}
              onDismiss={conv.dismiss}
              onRevert={conv.revert}
              onConfirmAll={conv.confirmAll}
              onDismissAll={conv.dismissAll}
              onHandleAll={(msg) =>
                conv.propose(
                  `Queued all ${(msg.actions ?? []).length} — approve what you want done.`,
                  (msg.actions ?? []).map((p) => p.action),
                )
              }
            />
          ))}
        </div>

        {/* Composer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {!canMutate && (
            <div
              className="t-meta"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                padding: '6px 10px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                color: C.textMuted,
              }}
            >
              <Lock size={12} />
              Read-only access — only editors can send commands.
            </div>
          )}
          {canMutate && fresh && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {prompts.map((prompt) => (
                <button
                  key={prompt}
                  className="glass-pill"
                  onClick={() => void conv.send(prompt)}
                  disabled={conv.busy}
                  style={{
                    cursor: conv.busy ? 'default' : 'pointer',
                    border: '1px solid rgba(255,0,255,0.22)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                  title="Send this prompt"
                >
                  <Sparkles size={11} color={C.magentaSoft} />
                  {prompt}
                </button>
              ))}
            </div>
          )}
          <GlassPanel variant="subtle" animate={false} style={{ padding: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="glass-input"
                placeholder={canMutate ? 'Ask Argo about this view…' : 'Read-only access — editors only'}
                value={composer}
                disabled={conv.busy || !canMutate}
                autoFocus
                onChange={(e) => setComposer(e.target.value)}
                onPaste={(e) => {
                  // Same roster-paste behavior as the full chat: a single-line
                  // <input> silently flattens pasted newlines, so multi-line
                  // pastes send whole (the backend reads them as an import).
                  if (conv.busy || !canMutate) return;
                  const pasted = e.clipboardData.getData('text');
                  if (pasted.trim().includes('\n')) {
                    e.preventDefault();
                    void conv.send(pasted);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                style={{ background: 'transparent', border: 'none', boxShadow: 'none', flex: 1 }}
              />
              <button
                className="glass-btn glass-btn--primary"
                style={{ padding: '8px 12px' }}
                onClick={submit}
                disabled={conv.busy || !composer.trim() || !canMutate}
              >
                {conv.busy ? (
                  <Loader2 size={13} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
                ) : (
                  <>
                    Send <CornerDownLeft size={13} />
                  </>
                )}
              </button>
            </div>
          </GlassPanel>
        </div>
      </GlassPanel>
    </div>
  );
}
